"use client";

import React from "react";
import { motion } from "framer-motion";
import { Wifi, MapPin, QrCode, Heart, MessageSquare, ArrowRight } from "lucide-react";

const cormorant = {
    className: "font-cormorant font-serif",
};

const fadeUp = {
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.7 },
};

function getIcon(iconName: string) {
    switch (iconName) {
        case "Wifi": return <Wifi className="w-3.5 h-3.5" />;
        case "MapPin": return <MapPin className="w-3.5 h-3.5" />;
        case "QrCode": return <QrCode className="w-3.5 h-3.5" />;
        case "Heart": return <Heart className="w-3.5 h-3.5" />;
        case "MessageSquare": return <MessageSquare className="w-3.5 h-3.5" />;
        case "ArrowRight": return <ArrowRight className="w-3.5 h-3.5" />;
        default: return null;
    }
}

function getIconBig(iconName: string) {
    switch (iconName) {
        case "Wifi": return <Wifi className="w-4 h-4 text-[#1C1C1C]" />;
        case "MapPin": return <MapPin className="w-4 h-4 text-[#1C1C1C]" />;
        case "QrCode": return <QrCode className="w-4 h-4 text-[#1C1C1C]" />;
        case "Heart": return <Heart className="w-4 h-4 text-[#1C1C1C]" />;
        case "MessageSquare": return <MessageSquare className="w-4 h-4 text-[#1C1C1C]" />;
        case "ArrowRight": return <ArrowRight className="w-4 h-4 text-[#1C1C1C]" />;
        default: return null;
    }
}

export interface GridItemSubTag {
    label: string;
    icon: string;
}

export interface DeliverablesCard {
    type: "large" | "small";
    label?: string;
    subLabel?: string;
    title: string;
    description: string;
    iconName?: string;
    subTags?: GridItemSubTag[];
}

export interface GuestiqoDeliverablesBlockProps {
    tag?: string;
    title?: string;
    cards?: DeliverablesCard[];
}

export default function GuestiqoDeliverablesBlock({
    tag = "// System Modules",
    title = "What We Designed & Engineered",
    cards = [
        {
            type: "large",
            label: "Core Deliverable",
            subLabel: "Mobile Guide Renderer",
            title: "The mobile guidebook engine",
            description: "A blazing fast, responsive front-end module capable of displaying complex property credentials, emergency steps, utility instructions, and customized layout items instantly on any mobile device.",
            subTags: [
                { label: "Wi-Fi Access", icon: "Wifi" },
                { label: "Check-in Steps", icon: "ArrowRight" },
                { label: "House Rules", icon: "QrCode" },
                { label: "Local Guide", icon: "MapPin" },
                { label: "Memory Wall", icon: "Heart" },
                { label: "Feedback Notes", icon: "MessageSquare" }
            ]
        },
        {
            type: "small",
            label: "Recommendations",
            title: "Local curation ledger",
            description: "Allows hosts to input their hand-picked recommendations for restaurants, hikes, and pubs, plotting them beautifully with metadata, custom tags, and navigation map coordinates.",
            iconName: "MapPin"
        },
        {
            type: "small",
            label: "Provisioning",
            title: "Instant QR routing",
            description: "Unique QR routing paths mapped to subdomain parameters, instantly resolving host datasets without loading screens or secondary redirection chains.",
            iconName: "QrCode"
        },
        {
            type: "small",
            label: "Sentiment Ledger",
            title: "The Memory Wall",
            description: "An elegant gallery flow where verified guests post quick photo moments and thank-you logs. Creates an authentic emotional ledger right on the guidebook.",
            iconName: "Heart"
        },
        {
            type: "small",
            label: "Optimization Loop",
            title: "Operational feedback",
            description: "Privacy-first checkout metrics gathering, yielding structured reviews on property cleanliness, amenities, and overall stay ratings directly to the host dashboard.",
            iconName: "MessageSquare"
        }
    ]
}: GuestiqoDeliverablesBlockProps) {
    return (
        <section className="py-24 px-6 md:px-12 lg:px-24 bg-[#FAF7F2] border-t border-b border-[#1C1C1C]/5 w-full" data-header-theme="dark">
            <div className="container mx-auto">
                <motion.div {...fadeUp} className="mb-16">
                    {tag && <span className="text-xs font-mono text-[#8C8276] uppercase tracking-widest block mb-4">{tag}</span>}
                    {title && <h2 className={`${cormorant.className} text-4xl md:text-6xl font-light text-[#1C1C1C] leading-tight`}>{title}</h2>}
                </motion.div>

                {/* Curated Asymmetrical Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
                    {cards.map((card, idx) => {
                        if (card.type === "large") {
                            return (
                                <div key={idx} className="md:col-span-2 bg-[#FDFBF7] border border-[#1C1C1C]/10 rounded-2xl p-8 md:p-10 flex flex-col justify-between group shadow-xs">
                                    <div className="space-y-4">
                                        <div className="inline-flex items-center gap-2">
                                            {card.label && (
                                                <span className="text-[10px] font-mono uppercase tracking-widest bg-[#1C1C1C] text-[#FDFBF7] px-2.5 py-1">
                                                    {card.label}
                                                </span>
                                            )}
                                            {card.subLabel && (
                                                <span className="text-[10px] font-mono uppercase text-[#8C8276] tracking-wider">
                                                    {card.subLabel}
                                                </span>
                                            )}
                                        </div>
                                        <h3 className={`${cormorant.className} text-3xl font-light text-[#1C1C1C] leading-tight`}>
                                            {card.title}
                                        </h3>
                                        <p className="text-[#5E5E5E] text-sm leading-relaxed max-w-lg font-light">
                                            {card.description}
                                        </p>
                                    </div>
                                    {card.subTags && card.subTags.length > 0 && (
                                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-8">
                                            {card.subTags.map((subTag, i) => (
                                                <div key={i} className="text-xs text-[#1C1C1C] bg-[#FAF7F2] border border-[#1C1C1C]/5 rounded-lg p-3 flex items-center justify-center gap-2 font-mono">
                                                    {getIcon(subTag.icon)}
                                                    <span>{subTag.label}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        } else {
                            return (
                                <div key={idx} className="bg-[#FDFBF7] border border-[#1C1C1C]/10 rounded-2xl p-8 flex flex-col justify-between group shadow-xs">
                                    <div className="space-y-4">
                                        {card.label && (
                                            <span className="text-[10px] font-mono uppercase text-[#8C8276] tracking-widest block">
                                                // {card.label}
                                            </span>
                                        )}
                                        <h3 className={`${cormorant.className} text-2xl font-medium text-[#1C1C1C]`}>
                                            {card.title}
                                        </h3>
                                        <p className="text-[#5E5E5E] text-xs leading-relaxed font-light">
                                            {card.description}
                                        </p>
                                    </div>
                                    {card.iconName && (
                                        <div className="mt-6 w-10 h-10 rounded-full bg-[#FAF7F2] border border-[#1C1C1C]/10 flex items-center justify-center">
                                            {getIconBig(card.iconName)}
                                        </div>
                                    )}
                                </div>
                            );
                        }
                    })}
                </div>
            </div>
        </section>
    );
}
